/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { uuid } from '@src/app/model/abstract-object';
import { Cycle } from '@src/app/model/cycle';
import { PurchaseDocument } from '@src/app/model/purchaseDocument';

/**
 * Builds _contents.csv - one line per purchase transaction, saying how many documents it should
 * have and where they were written.
 *
 * This is the file that makes the download worth having. Without it, an accountant who thinks a
 * document is missing has to diff a folder tree against the ledger CSVs by eye; with it, they
 * sort on the Status column. It is also how two suppliers that flattened to the same folder name
 * are told apart, because the supplier is named in full on every line.
 */
export class PurchaseDocContents {

    static readonly FILE_NAME = '_contents.csv';

    /**
     * One line per document, rather than per transaction, so each file can carry the name and
     * notes it was attached under. A transaction with several documents repeats across several
     * lines; one with none gets a single line with NO DOCUMENT in Status.
     */
    private static readonly COLUMNS = [
        'Type', 'Ref Nr', 'Date', 'Supplier', 'Reference', 'Amount', 'Documents Expected',
        'Folder', 'File Saved As', 'Document Name', 'Document Notes', 'Status', 'Transaction Notes'
    ];

    /** @param failed ids of the attachments that could not be written, so they can be flagged. */
    constructor(private cycle: Cycle, private rows: PurchaseDocument[], private failed: Set<uuid>) { }

    build(): string {
        const lines = [...this.heading(), PurchaseDocContents.COLUMNS];
        for (const txn of this.byTransaction()) {
            lines.push(...this.transactionLines(txn));
        }
        lines.push([], ...this.totals());

        return lines.map(line => this.csvLine(line)).join('\r\n');
    }

    private heading(): string[][] {
        return [
            ['OurOMC Purchase Documents'],
            ['Cycle', this.cycle.name, 'From', this.cycle.from, 'To', this.cycle.to],
            ['Generated', new Date().toString()],
            [],
            ['Every purchase, purchase credit note and cash purchase in the cycle is listed below,'],
            ['whether it has a document or not. Status says which is which. A transaction with'],
            ['more than one document takes a line per document.'],
            [],
        ];
    }

    /** Grouped so a transaction's documents stay together and share one expected count. */
    private byTransaction(): PurchaseDocument[][] {
        const grouped = new Map<uuid, PurchaseDocument[]>();
        for (const row of this.rows) {
            grouped.set(row.txnId, [...(grouped.get(row.txnId) ?? []), row]);
        }

        return [...grouped.values()];
    }

    /** Documents Expected repeats on every line of a transaction, so the count survives a sort. */
    private transactionLines(txn: PurchaseDocument[]): string[][] {
        const expected = txn.filter(row => row.hasDocument).length;

        return txn.map(row => this.documentLine(row, expected));
    }

    private documentLine(row: PurchaseDocument, expected: number): string[] {
        return [
            row.typeName, String(row.refNr), row.txnDate, row.supplierName ?? '',
            row.reference ?? '', String(row.amount), String(expected),
            row.hasDocument ? row.folder : '',
            row.saveAs ?? '',
            row.attachmentName ?? '',
            row.attachmentNotes ?? '',
            this.status(row),
            row.notes ?? '',
        ];
    }

    private status(row: PurchaseDocument): string {
        if (!row.hasDocument) {
            return 'NO DOCUMENT';
        }

        return this.failed.has(row.attachmentId) ? 'FAILED' : 'OK';
    }

    private totals(): string[][] {
        const documents = this.rows.filter(row => row.hasDocument);
        const missing = this.rows.filter(row => !row.hasDocument);

        return [
            ['Transactions', String(this.byTransaction().length)],
            ['Documents expected', String(documents.length)],
            ['Documents written', String(documents.length - this.failed.size)],
            ['Transactions with no document', String(missing.length)],
            ['Documents that failed to download', String(this.failed.size)],
        ];
    }

    /** Excel needs a quoted field for anything holding a comma, a quote or a line break. */
    private csvLine(values: string[]): string {
        return values.map(value => this.csvValue(value)).join(',');
    }

    private csvValue(value: string): string {
        const text = value ?? '';
        if (!/[",\r\n]/.test(text)) {
            return text;
        }

        return '"' + text.replace(/"/g, '""') + '"';
    }
}
